import React, { useState, useEffect, useRef } from "react";
import "./About.css";
import NavBar from "./NavBar";
import Footer from "./Footer";

// Font Awesome imports
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLightbulb,
  faBullseye,
  faRocket,
  faCrown,
  faMedal,
  faChartLine,
  faHandshake,
  faEye,
  faLocationDot,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { faLinkedin } from "@fortawesome/free-brands-svg-icons";
import { Link } from "react-router-dom";

const About = () => {
  const [isStatsVisible, setIsStatsVisible] = useState(false);
  const statsRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsStatsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isStatsVisible) {
      animateCounters();
    }
  }, [isStatsVisible]);

  const animateCounters = () => {
    const counters = document.querySelectorAll(".stat-number");

    counters.forEach((counter) => {
      const target = +counter.getAttribute("data-count");
      const duration = 2000;
      const frameDuration = 1000 / 60;
      const totalFrames = Math.round(duration / frameDuration);
      let frame = 0;

      counter.innerText = "0";

      const counterInterval = setInterval(() => {
        frame++;
        const progress = frame / totalFrames;
        const currentCount = Math.round(target * progress);

        if (frame === totalFrames) {
          clearInterval(counterInterval);

          const label = counter.nextElementSibling.innerText;
          if (label === "Billboards" || label === "Clients") {
            counter.innerText = currentCount + "+";
          } else if (label === "Satisfaction Rate") {
            counter.innerText = currentCount + "%";
          } else {
            counter.innerText = currentCount;
          }
        } else {
          counter.innerText = currentCount;
        }
      }, frameDuration);
    });
  };

  return (
    <div className="about-page">
      <NavBar />

      {/* Hero Section */}
      <section className="about-hero">
        <div className="hero-content container">
          <h1>About Spark Advertising</h1>
          <p>Transforming urban landscapes into impactful brand experiences</p>
          <div className="hero-stats">
            <div className="hero-stat">
              <FontAwesomeIcon icon={faEye} />
              <span>25M+ Daily Impressions</span>
            </div>
            <div className="hero-stat">
              <FontAwesomeIcon icon={faLocationDot} />
              <span>15 Major Cities</span>
            </div>
            <div className="hero-stat">
              <FontAwesomeIcon icon={faUsers} />
              <span>500+ Brands Served</span>
            </div>
          </div>
        </div>
        <div className="hero-overlay"></div>
      </section>

      {/* Mission Section */}
      <section className="mission-section">
        <div className="container">
          <div className="about-section-header">
            <span className="section-subtitle">Our Foundation</span>
            <h2>Driving Brand Visibility</h2>
            <p>We create advertising solutions that capture attention and deliver results</p>
          </div>
          <div className="mission-content">
            <div className="mission-card">
              <div className="card-icon">
                <FontAwesomeIcon icon={faLightbulb} />
              </div>
              <h3>Vision</h3>
              <p>
                To redefine outdoor advertising through innovative technologies and 
                strategic placements that transform how brands connect with audiences.
              </p>
            </div>
            <div className="mission-card">
              <div className="card-icon">
                <FontAwesomeIcon icon={faBullseye} />
              </div>
              <h3>Mission</h3>
              <p>
                To provide data-driven outdoor advertising solutions that maximize ROI 
                through premium locations, cutting-edge formats, and measurable results.
              </p>
            </div>
            <div className="mission-card">
              <div className="card-icon">
                <FontAwesomeIcon icon={faRocket} />
              </div>
              <h3>Our Approach</h3>
              <p>
                Combining audience analytics, strategic positioning, and creative excellence 
                to deliver campaigns that resonate and drive consumer action.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section ref={statsRef} className="stats-section">
        <div className="container">
          <div className="about-section-header">
            <span className="section-subtitle">By The Numbers</span>
            <h2>Our Impact</h2>
          </div>
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-number" data-count="200">
                0
              </div>
              <div className="stat-label">Premium Billboards</div>
            </div>
            <div className="stat-item">
              <div className="stat-number" data-count="13">
                0
              </div>
              <div className="stat-label">Years of Excellence</div>
            </div>
            <div className="stat-item">
              <div className="stat-number" data-count="500">
                0
              </div>
              <div className="stat-label">Satisfied Clients</div>
            </div>
            <div className="stat-item">
              <div className="stat-number" data-count="98">
                0
              </div>
              <div className="stat-label">Client Retention Rate</div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="values-section">
        <div className="container">
          <div className="about-section-header">
            <span className="section-subtitle">Our Principles</span>
            <h2>Core Values</h2>
            <p>The foundation of everything we do</p>
          </div>
          <div className="values-container">
            <div className="value-item">
              <div className="value-icon">
                <FontAwesomeIcon icon={faCrown} />
              </div>
              <div className="value-content">
                <h3>Excellence</h3>
                <p>
                  We maintain the highest standards in design, materials, and
                  maintenance for all our advertising spaces.
                </p>
              </div>
            </div>
            <div className="value-item">
              <div className="value-icon">
                <FontAwesomeIcon icon={faChartLine} />
              </div>
              <div className="value-content">
                <h3>Innovation</h3>
                <p>
                  We embrace cutting-edge technologies and creative approaches
                  to keep your brand ahead of the curve.
                </p>
              </div>
            </div>
            <div className="value-item">
              <div className="value-icon">
                <FontAwesomeIcon icon={faMedal} />
              </div>
              <div className="value-content">
                <h3>Impact</h3>
                <p>
                  Our billboards are positioned in high-traffic areas to
                  maximize visibility and drive measurable results.
                </p>
              </div>
            </div>
            <div className="value-item">
              <div className="value-icon">
                <FontAwesomeIcon icon={faHandshake} />
              </div>
              <div className="value-content">
                <h3>Partnership</h3>
                <p>
                  We work collaboratively with our clients to understand their goals and
                  deliver customized solutions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="timeline-section">
        <div className="container">
          <div className="about-section-header">
            <span className="section-subtitle">Our Journey</span>
            <h2>Company Milestones</h2>
            <p>From vision to industry leadership</p>
          </div>
          <div className="timeline">
            <div className="timeline-item">
              <div className="timeline-year">2010</div>
              <div className="timeline-content">
                <h4>Foundation</h4>
                <p>
                  VisionBoard Media was founded with a mission to transform urban advertising 
                  landscapes with strategic billboard locations.
                </p>
              </div>
            </div>
            <div className="timeline-item">
              <div className="timeline-year">2014</div>
              <div className="timeline-content">
                <h4>Strategic Expansion</h4>
                <p>
                  Expanded our portfolio to 50 premium billboards across major metropolitan areas 
                  and transportation corridors.
                </p>
              </div>
            </div>
            <div className="timeline-item">
              <div className="timeline-year">2018</div>
              <div className="timeline-content">
                <h4>Digital Innovation</h4>
                <p>
                  Launched our digital billboard division with interactive capabilities, 
                  real-time content updates, and dynamic scheduling.
                </p>
              </div>
            </div>
            <div className="timeline-item">
              <div className="timeline-year">2023</div>
              <div className="timeline-content">
                <h4>Market Leadership</h4>
                <p>
                  Recognized as the leading outdoor advertising provider with 200+ billboards 
                  and a nationwide presence serving Fortune 500 companies.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="team-section">
        <div className="container">
          <div className="about-section-header">
            <span className="section-subtitle">Our Leadership</span>
            <h2>Executive Team</h2>
            <p>The visionaries behind our success</p>
          </div>
          <div className="team-grid">
            <div className="team-member">
              <div className="member-image">
                <div className="image-placeholder"></div>
                <div className="member-social">
                  <Link to={""}><FontAwesomeIcon icon={faLinkedin} /></Link>
                </div>
              </div>
              <h3>Sarah Johnson</h3>
              <p className="member-role">CEO & Founder</p>
              <p className="member-desc">
                15+ years of experience in media and advertising leadership
              </p>
            </div>
            <div className="team-member">
              <div className="member-image">
                <div className="image-placeholder"></div>
                <div className="member-social">
                  <Link to={""}><FontAwesomeIcon icon={faLinkedin} /></Link>
                </div>
              </div>
              <h3>Michael Chen</h3>
              <p className="member-role">Chief Operations Officer</p>
              <p className="member-desc">
                Expert in outdoor media planning and large-scale campaign execution
              </p>
            </div>
            <div className="team-member">
              <div className="member-image">
                <div className="image-placeholder"></div>
                <div className="member-social">
                  <Link to={""}><FontAwesomeIcon icon={faLinkedin} /></Link>
                </div>
              </div>
              <h3>Elena Rodriguez</h3>
              <p className="member-role">Creative Director</p>
              <p className="member-desc">
                Award-winning creative director with a passion for visual storytelling
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}

      <Footer />
    </div>
  );
};

export default About;